import i18n from "@microdoing/shared/i18n";
import { useEffect } from "react";
import {
  cancelAllNotifications,
  scheduleGoalNotif,
} from "@microdoing/shared/services/notifService";
import {
  changeSignupInput,
  signupOnboardingEndRequest,
} from "@microdoing/shared/services/signup/actions";
import {
  getSignupGoalNumber,
  getSignupRequesting,
} from "@microdoing/shared/services/signup/selectors";
import hasNotificationPermission from "@microdoing/shared/utils/hasNotificationPermission";
import { changeUserInfo } from "@microdoing/shared/services/profileSettings/actions";
import { modifyUser } from "@microdoing/shared/services/user/actions";
import {
  MainText,
  Box,
  PressableOpacity,
  MainButton,
  MainTitle,
  MySafeAreaView,
} from "app/components/common/";
import React from "react";
import { StyleSheet, ScrollView, BackHandler } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import GoalBox from "./GoalBox";
import theme from "app/Theme/Theme";
import Theme from "app/constants/Theme";
import normalize from "app/utils/normalize";
import ArrowLeftIcon from "app/components/common/ArrowLeftIcon";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import FastImage from "react-native-fast-image";
import { getUserGoalNumber } from "@microdoing/shared/services/user/selectors";
import {
  SafeAreaView,
  useSafeAreaInsets,
} from "react-native-safe-area-context";

const LongDarkLogo = require("./../../../assets/images/long-dark-logo.png");

const getRemindersDaysWithGoalNumber = goalNumber => {
  const newReminderDays = {
    1: false,
    2: false,
    3: false,
    4: false,
    5: false,
    6: false,
    7: false,
  };

  if (goalNumber === 0) {
    return newReminderDays;
  } else if (goalNumber === 1) {
    newReminderDays[3] = true;
  } else if (goalNumber === 2) {
    newReminderDays[2] = true;
    newReminderDays[4] = true;
  } else if (goalNumber === 3) {
    newReminderDays[2] = true;
    newReminderDays[3] = true;
    newReminderDays[4] = true;
  } else if (goalNumber === 4) {
    newReminderDays[2] = true;
    newReminderDays[3] = true;
    newReminderDays[4] = true;
    newReminderDays[5] = true;
  } else if (goalNumber === 5) {
    newReminderDays[1] = true;
    newReminderDays[2] = true;
    newReminderDays[3] = true;
    newReminderDays[4] = true;
    newReminderDays[5] = true;
  }
  return newReminderDays;
};

const NO_GOAL = -1;

const styles = StyleSheet.create({
  scrollView: {
    flexGrow: 1,
    flex: 1,
    justifyContent: "flex-start",
    alignItems: "center",
    marginBottom: isWebDesktop ? 28 : 50,
  },
  webLongLogo: {
    height: normalize(16),
    width: normalize(110),
  },
});

const GOALS_OPTIONS = [1, 2, 3, 4, 5];

interface Props {
  navigation: any;
  route: any;
}

const WeeklyGoalScreen = ({ navigation, route }: Props) => {
  const isComingFromSignup = route?.params?.isComingFromSignup;

  const dispatch = useDispatch();
  const goalNumber = useSelector(getUserGoalNumber);

  const insets = useSafeAreaInsets();

  const requesting: boolean = useSelector(getSignupRequesting);

  useEffect(() => {
    // default settings
    const newReminderDays = getRemindersDaysWithGoalNumber(goalNumber);
    dispatch(
      changeUserInfo({
        reminderDays: newReminderDays,
      }),
    );

    if (isComingFromSignup) {
      dispatch(changeSignupInput({ goalNumber: 3 }));
      dispatch(
        modifyUser({
          goalNumber: 3,
          reminderDays: getRemindersDaysWithGoalNumber(3),
        }),
      );
    }

    BackHandler.addEventListener("hardwareBackPress", onBackScreen);
    return BackHandler.removeEventListener("hardwareBackPress", onBackScreen);
  }, []);

  const onPressGoal = goal => async () => {
    if (isComingFromSignup) {
      dispatch(changeSignupInput({ goalNumber: goal }));
    }

    const newReminderDays = getRemindersDaysWithGoalNumber(goal);

    dispatch(
      changeUserInfo({
        goalNumber: goal,
        reminderDays: newReminderDays,
      }),
    );

    dispatch(
      modifyUser({
        goalNumber: goal,
        reminderDays: newReminderDays,
      }),
    );

    const enabled = await hasNotificationPermission();

    if (!isComingFromSignup && goalNumber !== -1 && !enabled) {
      return navigation.navigate("AllowNotifications", {
        isComingFromSignup,
      });
    }
  };

  const checkAndSetNotifications = async () => {
    const enabled = await hasNotificationPermission();
    if (!enabled && goalNumber !== -1 && isComingFromSignup) {
      return navigation.navigate("AllowNotifications", {
        isComingFromSignup,
      });
    }
    await cancelAllNotifications();
    await scheduleGoalNotif();
  };

  const onBackScreen = () => {
    checkAndSetNotifications();
    return true;
  };

  const handlePressNext = async () => {
    const enabled = await hasNotificationPermission();
    if (!enabled && isComingFromSignup) {
      return checkAndSetNotifications();
    }

    if (enabled) {
      dispatch(modifyUser({ hasNotificationsEnabled: true }));
    }

    if (isComingFromSignup) {
      return dispatch(signupOnboardingEndRequest({}));
    } else {
      return navigation.goBack();
    }
  };

  const renderComponent = () => {
    return (
      <Box flex={1}>
        <Box
          flexDirection="row"
          width="100%"
          alignItems="flex-start"
          marginBottom="sm"
        >
          {!isComingFromSignup && (
            <PressableOpacity
              style={{
                marginLeft: normalize(-16),
              }}
              onPress={handlePressNext}
              hitSlop={Theme.HITSLOP}
            >
              <Box
                justifyContent="center"
                alignItems="center"
                marginRight="sm"
                marginLeft="sm"
                marginTop="xs"
              >
                <ArrowLeftIcon
                  color={theme.colors["neutral.900"]}
                  height={normalize(20)}
                  width={normalize(20)}
                />
              </Box>
            </PressableOpacity>
          )}
          <Box
            flexDirection="row"
            flex={1}
            alignSelf="center"
            alignItems={isWebDesktop ? "flex-start" : "center"}
            marginBottom={isWebDesktop ? "null" : "sm"}
          >
            <Box
              flexDirection="column"
              alignItems={isWebDesktop ? "flex-start" : "center"}
              justifyContent={isWebDesktop ? "flex-start" : "center"}
              width={isComingFromSignup ? "100%" : "auto"}
            >
              <MainTitle
                variant="l7-bold"
                textAlign={isWebDesktop ? "left" : "center"}
              >
                {i18n.t("weekly-goal-title")}
              </MainTitle>
              <MainText
                variant="l4-regular"
                textAlign={isWebDesktop ? "left" : "center"}
                marginTop="xxs"
              >
                {i18n.t("weekly-goal-subtitle")}
              </MainText>
            </Box>
          </Box>
        </Box>
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={styles.scrollView}
        >
          {GOALS_OPTIONS.map(el => (
            <GoalBox
              goalNumber={el}
              key={el}
              onPress={onPressGoal(el)}
              isSelected={goalNumber === el}
            />
          ))}
          <>
            <MainText
              variant="l4-medium"
              color="neutral.600"
              textAlign="center"
              fontStyle="italic"
              marginTop="s"
            >
              {isWebDesktop
                ? i18n.t("challenge-time-explanation-1-web")
                : i18n.t("challenge-time-explanation-1")}
            </MainText>
            {!isComingFromSignup && (
              <PressableOpacity onPress={onPressGoal(NO_GOAL)}>
                <MainText variant="l3-medium" textAlign="center" marginTop="xs">
                  {i18n.t("no-goal")}
                </MainText>
              </PressableOpacity>
            )}
          </>
        </ScrollView>
        {!!isComingFromSignup && (
          <Box width="100%" alignSelf="center">
            <MainButton
              text={i18n.t("validate-goal")}
              onPress={handlePressNext}
              isLoading={requesting}
            />
          </Box>
        )}
      </Box>
    );
  };

  if (isWebDesktop) {
    return (
      <Box
        flex={1}
        justifyContent="center"
        backgroundColor="neutral.white"
        paddingHorizontal="s"
      >
        <Box variant="webOnboarding" backgroundColor="neutral.white">
          <Box
            paddingLeft="webXXL"
            paddingTop={{ smallWeb: "l", webBig: "webXXL" }}
          >
            <FastImage
              resizeMode="contain"
              source={LongDarkLogo}
              style={styles.webLongLogo}
            />
          </Box>
          <Box flex={1} alignItems="center">
            <Box flex={1} width={normalize(344)} marginVertical="m">
              {renderComponent()}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <MySafeAreaView flex={1} backgroundColor="neutral.white">
      <Box variant="screenView" backgroundColor="neutral.white">
        {renderComponent()}
      </Box>
    </MySafeAreaView>
  );
};

export default WeeklyGoalScreen;
